import { Tooltip, Typography, Table } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { find, get } from 'lodash';
import moment from 'moment';
import React, { useState, useEffect, useMemo } from 'react';
import { useBookUnits } from 'store/booksUnits/selectors';
import { useExams } from 'store/exams/selectors';
import { useResources } from 'store/resources/selectors';
import { useStory } from 'store/story/selectors';
import { getNonAssignableMaskId, getNumberMask, isEbook, isVideo, shouldSkipLateDateForDiffStudent } from 'utils/lib/ebookHelper';
import CountdownTimer from 'components/CountdownTimer';
import { useCurrentClass } from 'store/classes/selectors';

const getBookDisplayName = (chapter, booksAllList, resources) => {
    let chapterName;
    if (isEbook(chapter, booksAllList)) {
        chapterName = get(find(booksAllList, ["id", chapter]), "description", "");
    } else {
        if (isVideo(chapter, resources)) {
            chapterName = "Video";
        }
    }
    return chapterName;
}

const GradebookPopoverContent = ({
    booksAllList,
    x,
    currentUser
}) => {
    const bookAllUnits = useBookUnits();
    const currentClass = useCurrentClass();
    const resources = useResources();
    const displayName = get(x, 'displayName', get(x, 'data.displayName', ''));
    const pageId = get(x, "data.page");
    const chapter = get(x, "data.chapter") || get(x, "assignment.chapter");
    let numberMasked;
    let nonAssignAbleMaskId;
    const checkBookAssignableOrNotAssignable = booksAllList?.find(book=> Number(book.id) === Number(x.data?.bookId)) || {};
    // for story
    const story = useStory();
    const storyBook = find(story, ["bookId", x.data?.bookId]) || {};
    const isStoryBook  = Object.entries(storyBook)?.length > 0 ; 
    // for exams
    const exams = useExams();
    const examBookIds = exams?.flatMap((entry) =>
        entry.exams.map((exam) => `${exam.bookId}`)
      );
    const isExamBook = examBookIds?.includes(x?.data?.bookId)
    const [showCountdownTimer, setShowCountdownTimer] = useState(false);
    const [isDiffInfoExpanded, setIsDiffInfoExpanded] = useState(false);

    const numberMaskForNonAssignable = checkBookAssignableOrNotAssignable?.isNotAssignable && !isExamBook && !isStoryBook;

    if(numberMaskForNonAssignable){
        // for non-assignble book
        const maskId = getNonAssignableMaskId(bookAllUnits,x);
        nonAssignAbleMaskId = maskId;
    }else{
        // for assignable book
        numberMasked = getNumberMask(chapter, pageId, booksAllList);
    }

    if (isVideo(chapter, resources)) {
        numberMasked = null;
    }
    const chapterName = getBookDisplayName(chapter, booksAllList, resources)

    useEffect(() => {
        if (x?.data?.id) {
            const finishLaterClicked = localStorage.getItem(`finishLater_${x.data.id}`);
            if (finishLaterClicked === 'true') {
                setShowCountdownTimer(true);
            }
        }
    }, [x?.data?.id]);
    const diffInfo = useMemo(() => {
        const classConfig = find(x.classes, ['id', get(currentClass, "id")]);
       if(classConfig?.diff){
        if(Array.isArray(classConfig?.diff?.groups) && classConfig?.diff?.groups?.length > 0){
            return classConfig?.diff?.groups.map(group => ({
                display_name: group.display_name,
                dueDate: moment(group.dueDateDif).format('MMM DD, YYYY hh:mm A'),
                startDate: moment(group.scheduleAtDiff).format('MMM DD, YYYY hh:mm A'),
                no_of_students: group.student?.length || 0,
            }));
        }

       }
        return null;
    }, [currentClass,x]);

    return (
        <div>
            <Tooltip placement='top' title={displayName}>
            <span className='text-wrap'>
            {displayName && displayName?.length > 50? displayName?.slice(0,50) + "..." : displayName}
            </span>
            </Tooltip>         
            <br />
            <Typography.Text strong>Book:</Typography.Text> {isStoryBook ? "story" : chapterName } 
                {numberMaskForNonAssignable ? (
            <>
                <br />
                <Typography.Text strong>Page: {nonAssignAbleMaskId?.shortName}</Typography.Text>
                <label>&nbsp;(Pg. {nonAssignAbleMaskId?.maskId})</label> 
            </>
            ) : (
            <>
                {numberMasked && !isStoryBook && (
                    <>
                        <br />
                        <Typography.Text strong>Page:</Typography.Text> {numberMasked}
                    </>
                )}
            </>
            )}
           
            <br />
            <Typography.Text strong>Start Date:</Typography.Text> {moment(get(x, "scheduleAt")).format('MMM DD, YYYY hh:mm A')}
            <br />

            <Typography.Text strong>Due Date:</Typography.Text> {moment(get(x, "dueDate")).format('MMM DD, YYYY hh:mm A')}
            <br />
            
            {get(x, "lateDate") && (<><Typography.Text strong>Last Date:</Typography.Text> {moment(get(x, "lateDate")).format('MMM DD, YYYY hh:mm A')}<br /></>)}
            {/* {x?.lateDate && !shouldSkipLateDateForDiffStudent(x, currentUser, currentClass) && (
                <>
                    <br />
                    <Typography.Text strong>Late Submission:</Typography.Text> {moment(get(x, "lateDate")).format('MMM DD, YYYY hh:mm A')}
                </>
            )} */}
            <Typography.Text strong>Total Retake:</Typography.Text> {x?.retakes}
            <br />
            
            {showCountdownTimer && currentUser?.role === 'student'  && (get(x, "dueDate") || (get(x, "lateDate") && !shouldSkipLateDateForDiffStudent(x, currentUser, currentClass))) && (
                <div style={{display: 'flex'}}>
                    <br />
                    <Typography.Text strong style={{marginRight: '2px'}}>Time left to submit:</Typography.Text>
                    <CountdownTimer 
                        style={{fontSize: '10px'}}
                        dueDate={get(x, "dueDate")} 
                        lateDate={shouldSkipLateDateForDiffStudent(x, currentUser, currentClass) ? null : get(x, "lateDate")}
                        onTimeUp={() => {
                            setShowCountdownTimer(false);
                            if (x?.data?.id) {
                                localStorage.removeItem(`finishLater_${x.data.id}`);
                            }
                        }}
                    />
                </div>
            )}
            {diffInfo && (
                <div style={{ marginTop: '12px' }}>
                    <div 
                        style={{ 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: 'rgba(0, 255, 19, 0.31)',
                            padding: '6px 8px',
                            borderRadius: '4px'
                        }}
                        onClick={() => setIsDiffInfoExpanded(!isDiffInfoExpanded)}
                    >
                        {isDiffInfoExpanded ? (
                            <CaretDownOutlined style={{ fontSize: '12px' }} />
                        ) : (
                            <CaretRightOutlined style={{ fontSize: '12px' }} />
                        )}
                        <Typography.Text strong>
                            Differentiated Info: ({diffInfo.length} {diffInfo.length === 1 ? 'group' : 'groups'})
                        </Typography.Text>
                    </div>
                    {isDiffInfoExpanded && (
                        <div style={{ marginTop: '8px' }}>
                            <Table 
                                dataSource={diffInfo} 
                                columns={[{
                                    title: 'Group Name',
                                    dataIndex: 'display_name',
                                    key: 'display_name',
                                },{
                                    title: 'Start Date',
                                    dataIndex: 'startDate',
                                    key: 'startDate',
                                },{
                                    title: 'Due Date',
                                    dataIndex: 'dueDate',
                                    key: 'dueDate',
                                }]}
                                pagination={false}
                                size="small"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
};

export default GradebookPopoverContent;